// Self-hosted Umami client. Independent of Mixpanel; additive only.
// All Umami logic lives here. Guarded by the build-time UMAMI_ENABLED flag —
// when off (or host/website-id missing) every export is a hard no-op and no
// script is injected. Runs inside the player's cross-origin iframe document,
// so injected tags attach to the iframe's own <head>.

// Build-time constants (see scripts/vite/define.mjs). UMAMI_ENABLED is
// injected as a boolean literal; host/website-id as string literals.
const UMAMI_ENABLED = process.env.UMAMI_ENABLED === true;
const UMAMI_HOST = process.env.UMAMI_HOST || "";
const UMAMI_WEBSITE_ID = process.env.UMAMI_WEBSITE_ID || "";

const UID_KEY = "pix_umami_uid";

let initialized = false;
let ready = false;
let failed = false;
type UmamiData = Record<string, unknown> | undefined;

let _untilUmami: { title: string; data: UmamiData }[] = [];

export function isUmamiEnabled(): boolean {
  return UMAMI_ENABLED === true && !!UMAMI_HOST && !!UMAMI_WEBSITE_ID;
}

// Persistent per-viewer id. localStorage can throw in partitioned/blocked
// iframe contexts — degrade to null (identify simply skipped) rather than throw.
export function getUmamiUid(): string | null {
  try {
    let id = window.localStorage.getItem(UID_KEY);
    if (!id) {
      id =
        window.crypto && typeof window.crypto.randomUUID === "function"
          ? window.crypto.randomUUID()
          : String(Date.now()) + Math.random().toString(16).slice(2);
      window.localStorage.setItem(UID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

// Inject script.js + recorder.js once. auto-track is off (iframe URL is
// synthetic); recorder.js drives replays + heatmaps for the website.
export function ensureUmamiInitialized() {
  if (initialized || !isUmamiEnabled()) {
    return;
  }
  initialized = true;

  const tracker = document.createElement("script");
  tracker.defer = true;
  tracker.src = `${UMAMI_HOST}/script.js`;
  tracker.setAttribute("data-website-id", UMAMI_WEBSITE_ID);
  tracker.setAttribute("data-host-url", UMAMI_HOST);
  tracker.setAttribute("data-auto-track", "false");
  tracker.onload = function () {
    ready = true;
    if (window.umami && typeof window.umami.identify === "function") {
      const uid = getUmamiUid();
      if (uid) {
        window.umami.identify(uid);
      }
    }
    // One manual pageview per experience load. data-auto-track is off (the
    // iframe URL is synthetic), so without this the Overview tab (views /
    // visits / visit duration) stays empty even though custom events fire.
    if (window.umami && typeof window.umami.track === "function") {
      window.umami.track();
    }
    flushUmamiDeferred();
  };
  // Host unreachable or tracker blocked (e.g. ad-blocker): stop buffering so
  // _untilUmami can't grow unbounded for the rest of the session.
  tracker.onerror = function () {
    failed = true;
    _untilUmami = [];
  };
  document.head.appendChild(tracker);

  const recorder = document.createElement("script");
  recorder.defer = true;
  recorder.src = `${UMAMI_HOST}/recorder.js`;
  recorder.setAttribute("data-website-id", UMAMI_WEBSITE_ID);
  document.head.appendChild(recorder);
}

// Route one event to Umami. beacon=true (terminal events) uses sendBeacon so the
// request survives page unload. Otherwise send via window.umami if the tracker is
// ready, else buffer until it loads.
export function umamiTrack(
  title: string,
  data: UmamiData,
  options?: { beacon?: boolean },
) {
  if (!isUmamiEnabled()) {
    return;
  }
  options = options || {};
  if (options.beacon) {
    sendUmamiBeacon(title, data);
    return;
  }
  if (ready && window.umami && typeof window.umami.track === "function") {
    window.umami.track(title, data);
  } else if (!failed) {
    _untilUmami.push({ title: title, data: data });
  }
}

export function flushUmamiDeferred() {
  if (!ready || !window.umami || typeof window.umami.track !== "function") {
    return;
  }
  const queued = _untilUmami;
  _untilUmami = [];
  for (let i = 0; i < queued.length; i++) {
    window.umami.track(queued[i].title, queued[i].data);
  }
}

// Manual /api/send POST for terminal events (Exit / Experience Unload). Mixpanel's
// {transport:'sendBeacon'} is Mixpanel-specific and does not apply to Umami.
//
// Uses fetch with keepalive rather than navigator.sendBeacon: sendBeacon forces
// the request's credentials mode to "include", and an application/json body is
// not CORS-safelisted, so cross-origin embeds (e.g. the MCP App) send a
// credentialed request that the browser rejects against Umami's wildcard
// Access-Control-Allow-Origin. keepalive survives page unload the same way but
// lets us set credentials:"omit" — matching Umami's own script.js/recorder.js.
function sendUmamiBeacon(title: string, data: UmamiData) {
  try {
    const body = {
      type: "event",
      payload: {
        website: UMAMI_WEBSITE_ID,
        hostname: window.location.hostname,
        url: window.location.pathname + window.location.search,
        name: title,
        data: data,
      },
    };
    fetch(`${UMAMI_HOST}/api/send`, {
      method: "POST",
      keepalive: true,
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch(function () {
      // best-effort; unload in progress
    });
  } catch {
    // best-effort; unload is in progress
  }
}
